import { z } from 'zod'

export const PRACTITIONER_QUALIFICATIONS = [
  { value: 'MD', label: 'MD (Medical Doctor)' },
  { value: 'DO', label: 'DO (Doctor of Osteopathic Medicine)' },
  { value: 'NP', label: 'NP (Nurse Practitioner)' },
  { value: 'PA', label: 'PA (Physician Assistant)' },
  { value: 'RN', label: 'RN (Registered Nurse)' },
] as const

export const SPECIALTIES_BY_QUALIFICATION = {
  NP: [
    { value: 'FNP', label: 'FNP – Family Nurse Practitioner' },
    { value: 'APRN', label: 'APRN – Advanced Practice Registered Nurse' },
    { value: 'AGNP', label: 'AGNP – Adult-Gerontology Nurse Practitioner' },
  ],
  RN: [{ value: 'APRN', label: 'APRN – Advanced Practice Registered Nurse' }],
} as const

// Where a state license is in its life (see the glossary's License Status). The demo's
// `Archived` value is deliberately absent until an archive action exists: adding a value
// later is one line, retiring a stored one is not.
export const LICENSE_STATUSES = [
  { value: 'preparing', label: 'Preparing' },
  { value: 'pending-application', label: 'Pending application' },
  { value: 'under-board-review', label: 'Under board review' },
  { value: 'action-required', label: 'Action required' },
  { value: 'approved', label: 'Approved' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'active', label: 'Active' },
  { value: 'denied', label: 'Denied' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'expired', label: 'Expired' },
] as const

// A note on a state license (see the glossary's Restriction). Informational only: it
// changes nothing about matching, pricing or prescribing.
export const LICENSE_RESTRICTIONS = [
  { value: 'none', label: 'None' },
  { value: 'requires-review', label: 'Requires review' },
] as const

// Where a CAQH Profile is in its life (see the glossary's Profile Status). Set by a person;
// Overdue is derived from the re-attestation due date in the browser, never stored.
export const CAQH_PROFILE_STATUSES = [
  { value: 'current', label: 'Current' },
  { value: 'attestation_due', label: 'Attestation due' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'not_enrolled', label: 'Not enrolled' },
] as const

// Where a Payer Enrollment is in its life (see the glossary's Enrollment Status). Only
// `active` counts for Allocation; Approved is the payer's yes before the enrollment is in
// force. No Expired: it is derived from the End date, never set.
export const ENROLLMENT_STATUSES = [
  { value: 'preparing', label: 'Preparing' },
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under-payer-review', label: 'Under payer review' },
  { value: 'action-required', label: 'Action required' },
  { value: 'approved', label: 'Approved' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'denied', label: 'Denied' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'terminated', label: 'Terminated' },
] as const

// The Languages a Practitioner can hold a visit in (see the glossary): the Profile tab's
// fixed list, BCP-47 coded as `Practitioner.communication` stores them. No "Other" and no
// default; an empty list means not recorded, never English.
export const PRACTITIONER_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'zh', label: 'Chinese (Mandarin)' },
  { value: 'yue', label: 'Chinese (Cantonese)' },
  { value: 'tl', label: 'Tagalog' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'ko', label: 'Korean' },
  { value: 'ru', label: 'Russian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ht', label: 'Haitian Creole' },
  { value: 'hi', label: 'Hindi' },
  { value: 'de', label: 'German' },
  { value: 'pl', label: 'Polish' },
  { value: 'it', label: 'Italian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'ja', label: 'Japanese' },
  { value: 'fa', label: 'Persian (Farsi)' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'te', label: 'Telugu' },
  { value: 'bn', label: 'Bengali' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ase', label: 'American Sign Language' },
] as const

export type QualificationCode = (typeof PRACTITIONER_QUALIFICATIONS)[number]['value']
export type SpecialtyCode = 'FNP' | 'AGNP' | 'APRN'
export type LicenseStatus = (typeof LICENSE_STATUSES)[number]['value']
export type LicenseRestriction = (typeof LICENSE_RESTRICTIONS)[number]['value']
export type CaqhProfileStatus = (typeof CAQH_PROFILE_STATUSES)[number]['value']
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number]['value']
export type LanguageCode = (typeof PRACTITIONER_LANGUAGES)[number]['value']

export const PROFESSIONAL_ROLE_CODES = PRACTITIONER_QUALIFICATIONS.map((q) => q.value)
const SPECIALTY_CODES: readonly SpecialtyCode[] = ['FNP', 'AGNP', 'APRN']
const LICENSE_STATUS_CODES = LICENSE_STATUSES.map((s) => s.value)
const LICENSE_RESTRICTION_CODES = LICENSE_RESTRICTIONS.map((r) => r.value)
const CAQH_PROFILE_STATUS_CODES = CAQH_PROFILE_STATUSES.map((s) => s.value)
const ENROLLMENT_STATUS_CODES = ENROLLMENT_STATUSES.map((s) => s.value)
const LANGUAGE_CODES = PRACTITIONER_LANGUAGES.map((l) => l.value)

// The Professional Role and the Specialty (see the glossary): one each per Practitioner,
// shared by every state license. Reused by the Profile tab's and the invite modal's forms.
export const professionalRoleSchema = z.enum(PROFESSIONAL_ROLE_CODES, {
  error: 'Select a Professional role',
})
export const specialtySchema = z.enum(SPECIALTY_CODES)

// The Languages as the credentials writer, the get-one response, the list row and the
// Profile tab's form carry them: the list's codes, in the order picked.
export const languagesSchema = z.array(z.enum(LANGUAGE_CODES))

// A license with no recorded status is Active (so no existing provider drops out of
// matching or pricing on deploy), and so is one whose stored code is no longer in the list.
export function normalizeLicenseStatus(raw: string | undefined): LicenseStatus {
  return (LICENSE_STATUS_CODES as readonly string[]).includes(raw ?? '')
    ? (raw as LicenseStatus)
    : 'active'
}

// A license with no recorded restriction has None; so does one with an unrecognised code.
export function normalizeLicenseRestriction(raw: string | undefined): LicenseRestriction {
  return (LICENSE_RESTRICTION_CODES as readonly string[]).includes(raw ?? '')
    ? (raw as LicenseRestriction)
    : 'none'
}

// Specialties a given qualification may carry. Qualifications absent here (MD,
// DO, PA) have no specialty. Used by both the contract refine and the web form.
export function specialtyCodesFor(qualification: string | undefined): SpecialtyCode[] {
  if (!qualification || !(qualification in SPECIALTIES_BY_QUALIFICATION)) return []
  return SPECIALTIES_BY_QUALIFICATION[
    qualification as keyof typeof SPECIALTIES_BY_QUALIFICATION
  ].map((s) => s.value)
}

// The Professional Role with its Specialty as the console prints them, "NP · FNP"; empty when
// no role is set (a Specialty never prints without one). One formatter, so the Provider
// Operations list and the members table's hover card can never drift.
export function formatProfessionalRole(
  professionalRole: QualificationCode | null | undefined,
  specialty: SpecialtyCode | null | undefined,
): string {
  if (!professionalRole) return ''
  return specialty ? `${professionalRole} · ${specialty}` : professionalRole
}

// USPS 2-letter codes for the 50 US states. Owned here at the contract layer so
// the API boundary validates license states and every downstream consumer (the
// qualification read helpers, the future slot filter, the web license editor)
// shares one canonical list.
export const US_STATE_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const

export type USStateCode = (typeof US_STATE_CODES)[number]

// A canonical `Binary/<id>` reference, the only url the Supporting Document and photo carry.
const BINARY_REFERENCE = /^Binary\/[A-Za-z0-9._-]+$/

// The Supporting Document on a license (see the glossary): a FHIR Attachment subset. The bytes
// live in a Binary the console uploads with its own Medplum client; the row carries the
// canonical `Binary/<id>` reference and never the presigned URL Medplum hands out on read, so
// two reads of one license compare equal. `creation` is the upload instant set by the browser.
export const licenseDocumentSchema = z.object({
  url: z.string().regex(BINARY_REFERENCE, 'document url must be a Binary/<id> reference'),
  contentType: z.string().min(1).max(200),
  title: z.string().min(1).max(300),
  creation: z.iso.datetime(),
})

export type LicenseDocument = z.infer<typeof licenseDocumentSchema>

// An Org Member's photo: the FHIR Practitioner photo, first element, the same slot the
// member's own avatar menu writes. Canonical `Binary/<id>` as for the Supporting Document;
// `title` is the original file name.
export const practitionerPhotoSchema = z.object({
  url: z.string().regex(BINARY_REFERENCE, 'photo url must be a Binary/<id> reference'),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp'], {
    error: 'photo must be a JPEG, PNG or WebP image',
  }),
  title: z.string().min(1).max(300),
})

export type PractitionerPhoto = z.infer<typeof practitionerPhotoSchema>

// An Org Member's Professional Liability Coverage (see the glossary). Every field may be blank,
// including all of them: the console records what is known today. `null` is a field the card
// cleared. Reused for the request and the get-one response, so a tab can re-send what it
// fetched. Unrefined so it stays extendable; the date order is `collectLiabilityIssues`.
export const liabilityCoverageSchema = z.object({
  // Trimmed at the boundary (and by the console's resolver, which extends this schema), so a
  // whitespace-only policy name reads as empty and is not stored.
  policyName: z.string().trim().max(200).nullish(),
  coverageStart: z.iso.date().nullish(),
  coverageEnd: z.iso.date().nullish(),
  // The coverage's Supporting Document, at most one, as on a license; `null` is the card
  // clearing the slot.
  document: licenseDocumentSchema.nullish(),
})

export type LiabilityCoverage = z.infer<typeof liabilityCoverageSchema>

// An Org Member's CAQH Profile (see the glossary), kept as the coverage is: every field may be
// blank, including all of them; `null` is a field the tab cleared. Reused for the request and
// the get-one response. Unrefined so it stays extendable; the date order is `collectCaqhIssues`.
export const caqhProfileSchema = z.object({
  // Trimmed at the boundary (and by the console's resolver, which reuses this schema), so
  // whitespace-only input reads as empty and is not stored. No format rule: CAQH's own.
  providerId: z.string().trim().max(50).nullish(),
  status: z.enum(CAQH_PROFILE_STATUS_CODES).nullish(),
  lastAttestation: z.iso.date().nullish(),
  reattestationDue: z.iso.date().nullish(),
  notes: z.string().trim().max(1000).nullish(),
})

export type CaqhProfile = z.infer<typeof caqhProfileSchema>

// One row = one state license: only what differs per state. The degree and specialty are
// the practitioner's Professional Role and Specialty (`memberCredentialsSchema`), not the
// row's. Reused for both the request input (memberCredentialsSchema) and the list response
// (practitionerRowSchema) so the web editor reads/writes one shape.
export const licenseRowSchema = z.object({
  // A friendly `error` message so an incomplete license row shows "Select a
  // state" instead of zod's default "Invalid option: expected one of …".
  state: z.enum(US_STATE_CODES, { error: 'Select a state' }),
  // The free-text fields are trimmed at the boundary (and by the console's resolver, which
  // shares this schema), so whitespace-only input reads as empty and is not stored.
  licenseNumber: z.string().trim().optional(),
  // `.nullish()`: the Mantine DatePickerInput emits `null` when cleared, and the
  // field is optional; handlers treat a falsy value as "no expiry".
  expiryDate: z.iso.date().nullish(),
  // Absent reads as `active` (see `normalizeLicenseStatus`); the slim invite card omits it.
  status: z.enum(LICENSE_STATUS_CODES).optional(),
  // Absent reads as `none` (see `normalizeLicenseRestriction`); the slim invite card omits it.
  restriction: z.enum(LICENSE_RESTRICTION_CODES).optional(),
  // The start of the license's valid period; `.nullish()` for the same reason as `expiryDate`.
  effectiveDate: z.iso.date().nullish(),
  // The verification record: how, when and by whom the license was last checked.
  verificationSource: z.string().trim().max(200).optional(),
  lastVerifiedDate: z.iso.date().nullish(),
  verifiedBy: z.string().trim().max(200).optional(),
  // At most one Supporting Document per license; `null` is the card clearing the slot.
  document: licenseDocumentSchema.nullish(),
})

export type LicenseRow = z.infer<typeof licenseRowSchema>

// One row = one Payer Enrollment (see the glossary). Reused for the credentials update and
// the get-one response, as a license row is, so the Payer Enrollments tab reads/writes one
// shape. Friendly `error` messages on the required fields, so an incomplete card shows
// "Select a Payer" instead of zod's default. The cross-row rules are
// `collectPayerEnrollmentIssues`.
export const payerEnrollmentRowSchema = z.object({
  // The Billing Organization's id: one of the caller's Provider Operations scope, checked by
  // the update handler (403 otherwise), never here.
  billingOrganizationId: z
    .string({ error: 'Select a Billing Organization' })
    .min(1, { error: 'Select a Billing Organization' }),
  state: z.enum(US_STATE_CODES, { error: 'Select a state' }),
  // The free-text fields are trimmed at the boundary (and by the console's resolver, which
  // shares this schema), so a whitespace-only Payer reads as missing and the rest as empty.
  payer: z.string({ error: 'Enter a Payer' }).trim().min(1, { error: 'Enter a Payer' }).max(200),
  productNetwork: z.string().trim().max(200).optional(),
  status: z.enum(ENROLLMENT_STATUS_CODES, { error: 'Select an Enrollment Status' }),
  effectiveDate: z.iso.date({ error: 'Select an Effective date' }),
  // `.nullish()`: the Mantine DatePickerInput emits `null` when cleared.
  endDate: z.iso.date().nullish(),
  verifiedDate: z.iso.date().nullish(),
  verifiedBy: z.string().trim().max(200).optional(),
  approvalReference: z.string().trim().max(200).optional(),
  // At most one Supporting Document per enrollment; `null` is the card clearing the slot.
  document: licenseDocumentSchema.nullish(),
  // The catalog payer's ID (`/insurance/payers-catalog`), set with the name when the tab's
  // Payer is picked. Optional here so rows stored before the Payer became pick-only still read.
  payerId: z.string().trim().max(50).optional(),
})

export type PayerEnrollmentRow = z.infer<typeof payerEnrollmentRowSchema>

// What a save carries: the row with its Payer picked from the catalog, so the payer ID is
// set. A stored row without one shows its card with the Payer blank until it is picked again.
export const payerEnrollmentRowInputSchema = payerEnrollmentRowSchema.extend({
  payerId: z.string({ error: 'Select a Payer' }).trim().min(1, { error: 'Select a Payer' }).max(50),
})

export type PayerEnrollmentRowInput = z.infer<typeof payerEnrollmentRowInputSchema>

// The NPI as the console's text field edits it: exactly 10 digits, or blank for none. One
// rule for the invite modal, Provider Operations and My Profile, worded for the field's own
// error line. A stored NPI that breaks it is refused only when the member is next edited.
export const npiNumberSchema = z
  .string()
  .trim()
  .regex(/^(\d{10})?$/, {
    message: 'Must contain exactly 10 digits',
  })

export const memberCredentialsSchema = z.object({
  npiNumber: npiNumberSchema.optional(),
  // `null` is none; absent reads the same (the batch invite sends neither). A member may
  // have no Professional Role, but not a license without one (`collectCredentialIssues`).
  professionalRole: professionalRoleSchema.nullish(),
  specialty: specialtySchema.nullish(),
  // A member may have no licenses (they simply won't be insurance-routable yet).
  licenses: z.array(licenseRowSchema).default([]),
})

export type MemberCredentials = z.infer<typeof memberCredentialsSchema>

// Whether two license sets hold the same Credentials, ignoring row order. Shared by the
// credentials writer (to name the changed fields on its log line) and the web detail page
// (to skip the write when nothing changed), so both agree on what counts as a change.
export function sameLicenseSets(a: readonly LicenseRow[], b: readonly LicenseRow[]): boolean {
  const key = (row: LicenseRow) =>
    [
      row.state,
      row.licenseNumber ?? '',
      row.expiryDate ?? '',
      normalizeLicenseStatus(row.status),
      normalizeLicenseRestriction(row.restriction),
      row.effectiveDate ?? '',
      row.verificationSource ?? '',
      row.lastVerifiedDate ?? '',
      row.verifiedBy ?? '',
      // The document by reference only: a renamed title or a re-sent creation is not a change.
      row.document?.url ?? '',
    ].join('|')
  const sorted = (rows: readonly LicenseRow[]) => rows.map(key).sort().join('\n')
  return sorted(a) === sorted(b)
}

// The refusal of a license for a member with no Professional Role, also the Licenses tab's
// hint while none is stored: one sentence, so the two can never drift. Worded for the role
// select's own error line (the invite modal, the Profile tab) as much as for the hint, and it
// names no tab, as the invite modal has none.
export const LICENSES_NEED_PROFESSIONAL_ROLE_MESSAGE =
  'Select a Professional role: licenses need one'

// Cross-row rules shared by the invite + update contracts. Kept as a pure
// function returning issues (rather than a superRefine) so `memberCredentialsSchema`
// stays a ZodObject that both consumers can `.extend()`, and so the rules are
// unit-testable in isolation.
export function collectCredentialIssues(
  data: Pick<MemberCredentials, 'licenses' | 'professionalRole' | 'specialty'>,
): { path: (string | number)[]; message: string }[] {
  const issues: { path: (string | number)[]; message: string }[] = []
  const { professionalRole, specialty } = data
  const licenses = data.licenses ?? []

  // A Specialty must be one the Professional Role carries (none for MD, DO and PA).
  if (specialty && !specialtyCodesFor(professionalRole ?? undefined).includes(specialty)) {
    issues.push({
      path: ['specialty'],
      message: professionalRole
        ? `${specialty} is not a valid specialty for ${professionalRole}`
        : 'Set the Professional role before choosing a specialty',
    })
  }

  // A license never exists without a degree; the role itself is optional.
  if (licenses.length > 0 && !professionalRole) {
    issues.push({ path: ['professionalRole'], message: LICENSES_NEED_PROFESSIONAL_ROLE_MESSAGE })
  }

  const seen = new Set<string>()
  licenses.forEach((row, index) => {
    // One license per state — duplicate states would create ambiguous routing.
    if (seen.has(row.state)) {
      issues.push({
        path: ['licenses', index, 'state'],
        message: `Duplicate license for ${row.state}`,
      })
    }
    seen.add(row.state)

    // The valid period must not end before it starts. ISO dates compare as strings.
    if (row.effectiveDate && row.expiryDate && row.effectiveDate > row.expiryDate) {
      issues.push({
        path: ['licenses', index, 'effectiveDate'],
        message: 'Effective date is after the expiration date',
      })
    }
  })

  return issues
}

// The Professional Liability Coverage rule, shared by the credentials writer's contract and the
// Profile tab's form, as `collectCredentialIssues` is for the licenses: the period must not end
// before it starts. Only checked when both dates are set.
export function collectLiabilityIssues(data: {
  liability?: LiabilityCoverage | null
}): { path: (string | number)[]; message: string }[] {
  const { coverageStart, coverageEnd } = data.liability ?? {}
  return coverageStart && coverageEnd && coverageStart > coverageEnd
    ? [
        {
          path: ['liability', 'coverageStart'],
          message: 'Coverage start is after the coverage end',
        },
      ]
    : []
}

// The CAQH Profile rule, shared the same way: the re-attestation may not fall due before the
// last attestation. Only checked when both dates are set; the issue lands on the due date.
export function collectCaqhIssues(data: {
  caqh?: CaqhProfile | null
}): { path: (string | number)[]; message: string }[] {
  const { lastAttestation, reattestationDue } = data.caqh ?? {}
  return lastAttestation && reattestationDue && reattestationDue < lastAttestation
    ? [
        {
          path: ['caqh', 'reattestationDue'],
          message: 'Re-attestation due is before the last attestation',
        },
      ]
    : []
}

// The Payer Enrollment cross-row rules, shared the same way by the credentials update
// contract and the Payer Enrollments tab's form: the period must not end before it starts,
// and no two rows may record the same Billing Organization, State, Payer and Product /
// Network, the two text parts compared trimmed and lower-cased. Each issue lands on the
// offending row's field so the card shows it inline.
export function collectPayerEnrollmentIssues(data: {
  payerEnrollments?: readonly PayerEnrollmentRow[] | null
}): { path: (string | number)[]; message: string }[] {
  const issues: { path: (string | number)[]; message: string }[] = []
  const seen = new Set<string>()
  ;(data.payerEnrollments ?? []).forEach((row, index) => {
    // JSON, not a joined string: free text may contain any separator.
    const key = JSON.stringify([
      row.billingOrganizationId,
      row.state,
      row.payer.trim().toLowerCase(),
      (row.productNetwork ?? '').trim().toLowerCase(),
    ])
    if (seen.has(key)) {
      issues.push({
        path: ['payerEnrollments', index, 'payer'],
        message:
          'Duplicate enrollment: same Billing Organization, State, Payer and Product / Network',
      })
    }
    seen.add(key)

    // ISO dates compare as strings.
    if (row.endDate && row.endDate < row.effectiveDate) {
      issues.push({
        path: ['payerEnrollments', index, 'endDate'],
        message: 'End date is before the Effective date',
      })
    }
  })
  return issues
}
